import { ShieldAlert, ShieldCheck, ShieldQuestionMark } from 'lucide-react';
import { Alert, Tooltip } from '@waymail/ui';
import {
  avisosDoRemetente,
  nivelDeAutenticacao,
  rotuloDoVeredicto,
  type NivelDeAutenticacao,
  type ResultadosDeAutenticacao,
} from '../lib/sinaisDeAutenticacao';

interface Endereco {
  name: string;
  address: string;
}

const ROTULO: Record<NivelDeAutenticacao, string> = {
  verificado: 'Domínio do remetente confirmado',
  atencao: 'Atenção: a autenticação falhou',
  desconhecido: 'Remetente não verificado',
};

const RESSALVA: Record<NivelDeAutenticacao, string> = {
  verificado:
    'O servidor da sua caixa confirmou que o domínio do remetente autorizou o envio. Isso não garante que a mensagem é legítima.',
  atencao: 'O servidor da sua caixa não confirmou que o remetente é quem diz ser.',
  desconhecido: 'O servidor da sua caixa não confirmou que o remetente é quem diz ser.',
};

/**
 * Selo discreto ao lado do remetente (WYM-09, ADR 0051). Mostra o veredito que
 * o servidor receptor registrou para SPF, DKIM e DMARC; nao verifica nada e
 * nunca afirma que o remetente e legitimo.
 */
export function SeloDeAutenticacao({ auth }: { auth: ResultadosDeAutenticacao | null }) {
  const nivel = nivelDeAutenticacao(auth);
  const Icone =
    nivel === 'verificado' ? ShieldCheck : nivel === 'atencao' ? ShieldAlert : ShieldQuestionMark;
  const cor =
    nivel === 'verificado' ? 'text-success' : nivel === 'atencao' ? 'text-error' : 'text-tertiary';

  const conteudo = (
    <span className="block text-[12px] leading-5">
      <span className="block font-semibold">{ROTULO[nivel]}</span>
      {auth === null ? (
        <span className="block">
          Não há resultado de SPF, DKIM e DMARC do servidor da sua caixa.
        </span>
      ) : (
        <>
          <span className="block">SPF: {rotuloDoVeredicto(auth.spf)}</span>
          <span className="block">DKIM: {rotuloDoVeredicto(auth.dkim)}</span>
          <span className="block">DMARC: {rotuloDoVeredicto(auth.dmarc)}</span>
        </>
      )}
      <span className="mt-1 block opacity-80">{RESSALVA[nivel]}</span>
    </span>
  );

  return (
    <Tooltip content={conteudo}>
      <span tabIndex={0} role="img" aria-label={ROTULO[nivel]} className={cor}>
        <Icone size={15} strokeWidth={1.8} aria-hidden="true" />
      </span>
    </Tooltip>
  );
}

/**
 * O endereco real do remetente, sempre a vista, e os avisos de imitacao: nome
 * de exibicao com outro e-mail dentro, ou `Reply-To` em outro dominio.
 */
export function EnderecoEAvisosDoRemetente({
  de,
  replyTo,
}: {
  de: Endereco | null;
  replyTo: readonly Endereco[];
}) {
  if (de === null) return null;
  const avisos = avisosDoRemetente(de, replyTo);

  return (
    <>
      <p className="mt-2 text-[12px] break-all text-tertiary">Enviado por {de.address}</p>
      {avisos.nomeImitaEndereco !== null && (
        <Alert tone="warning" className="mt-3">
          O nome do remetente mostra o endereço {avisos.nomeImitaEndereco}, mas a mensagem veio de{' '}
          {de.address}. Golpes usam esse truque; confirme antes de responder ou clicar.
        </Alert>
      )}
      {avisos.respostaParaOutroDominio.length > 0 && (
        <Alert tone="warning" className="mt-3">
          Ao responder, sua mensagem iria para {avisos.respostaParaOutroDominio.join(', ')}, que não
          é o endereço de quem enviou ({de.address}). Confira antes de responder.
        </Alert>
      )}
    </>
  );
}
